// libraries
import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { Grid, IconButton, InputAdornment, Typography, Stack } from '@mui/material_modern'
import DeleteIcon from '@mui/icons-material_modern/Delete'

// queries
import { useGetInventoryMaterialTypeQuery } from 'services/inventory'

// components
import Input from 'components/generic/form/Input'
import MaterialAutocomplete from 'components/generic/form/AsyncAutocomplete/MaterialAutocomplete'

// helpers and utils
import { parseNumberObject, setDecimalPrecision } from 'utils/helpers'
import { addToTime } from 'utils/data-manipulation/common'

const RegradeMaterialRow = ({
  index,
  materialGroup,
  disabled,
  showRemoveButton,
  onRemove,
  effectiveDate,
  costOfMaterial,
  setCostOfMaterial,
  tosQuantity,
}) => {
  const [materialTypeInfo, setMaterialTypeInfo] = useState({})
  const { enableFourDecimalPrecisionInInventory } = useSelector(state => state.featureFlags?.variables || {})

  const { control, watch, setValue, formState, setError, clearErrors } = useFormContext()

  const { errors } = formState
  const materialNameField =
    materialGroup === 'from' ? `${materialGroup}.material` : `${materialGroup}.${index}.material`
  const materialWatch = watch(materialNameField)
  const quantityWatch = watch(
    materialGroup === 'from' ? `${materialGroup}.quantity` : `${materialGroup}.${index}.quantity`,
  )

  const { defaultYardUm } = useSelector(state => state.settings)

  const { data: inventoryMaterialTypeData } = useGetInventoryMaterialTypeQuery(
    // use 10 milliseconds before the selected effectiveDate
    {
      materialTypeId: materialWatch?.id,
      filter: JSON.stringify({ effectiveDate: addToTime(effectiveDate, -10).toISOString() }),
    },
    {
      skip: !materialWatch?.id,
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    if (materialWatch?.id && inventoryMaterialTypeData) {
      setMaterialTypeInfo({
        ...materialTypeInfo,
        wipAvgCost: parseNumberObject(inventoryMaterialTypeData?.wipAvgCost, {
          format: 'price',
          enableFourDecimalPrecisionInInventory,
          unitOverride: defaultYardUm,
        }),
        wip: parseNumberObject(inventoryMaterialTypeData?.wip, { format: 'weight', unitOverride: defaultYardUm }),
        wipCost: parseNumberObject(inventoryMaterialTypeData?.wipCost, {
          format: 'value',
          enableFourDecimalPrecisionInInventory,
        }),
      })
      if (materialGroup === 'from') {
        setCostOfMaterial(parseNumberObject(inventoryMaterialTypeData?.wipAvgCost) * parseInt(quantityWatch, 10))
      }
    }
  }, [materialWatch?.id, inventoryMaterialTypeData, quantityWatch])

  const rowTotal = useMemo(() => {
    if (materialGroup === 'from') return costOfMaterial
    if (!tosQuantity) return 0
    return (quantityWatch / tosQuantity) * costOfMaterial
  }, [costOfMaterial, quantityWatch, tosQuantity])

  return (
    <Grid container mt={1} spacing={2} alignItems="center">
      <Grid container item xs={11} spacing={2}>
        <Grid item md={4} xs={12}>
          <MaterialAutocomplete
            label="Material"
            placeholder="Search by Material Name"
            variant="outlined"
            InputProps={{ name: materialNameField }}
            defaultValue={{
              commonName: materialWatch?.commonName,
              code: materialWatch?.code,
              materialTypeId: materialWatch?.id,
            }}
            onChange={material => {
              if (material) {
                setValue(
                  materialNameField,
                  {
                    id: material.id,
                    commonName: material.commonName,
                    code: material.code,
                  },
                  { shouldDirty: true },
                )
                clearErrors(materialNameField)
              } else {
                setMaterialTypeInfo({})
                setError(materialNameField, { type: 'custom', message: 'Material is required.' })
              }
            }}
            disabled={disabled}
            TextFieldProps={{
              error: materialGroup === 'from' ? !!errors?.from?.material : !!errors?.to?.[index]?.material,
              helperText:
                materialGroup === 'from' && errors
                  ? errors?.from?.material?.message
                  : errors?.to?.[index]?.material?.message,
            }}
          />
          {materialTypeInfo?.wipAvgCost ? (
            <Typography variant="caption" color="textSecondary">
              Avg. Cost: {materialTypeInfo?.wipAvgCost || '-'}
            </Typography>
          ) : null}
        </Grid>
        <Grid item md={4} xs={12}>
          <Input
            name={materialGroup === 'from' ? `${materialGroup}.quantity` : `${materialGroup}.${index}.quantity`}
            label="Quantity"
            defaultValue=""
            placeholder=""
            type="number"
            pattern="[0-9]*"
            control={control}
            disabled={disabled}
            InputProps={{
              endAdornment: <InputAdornment position="end">{defaultYardUm}</InputAdornment>,
              inputProps: {
                step: 1,
                min: 0,
              },
            }}
            error={materialGroup === 'from' ? !!errors?.from?.quantity : !!errors?.to?.[index]?.quantity}
            helperText={
              materialGroup === 'from' && !!errors
                ? errors?.from?.quantity?.message
                : errors?.to?.[index]?.quantity?.message
            }
          />
          <Stack direction="row" spacing={2}>
            {materialTypeInfo?.wip ? (
              <Typography variant="caption" color="textSecondary">
                WIP: {materialTypeInfo?.wip || '-'}
              </Typography>
            ) : null}
            {materialTypeInfo?.wipCost ? (
              <Typography variant="caption" color="textSecondary">
                {`(Cost: ${materialTypeInfo?.wipCost || '-'})`}
              </Typography>
            ) : null}
          </Stack>
        </Grid>
        <Grid item md={4} xs={12}>
          <Stack direction="row" justifyContent="space-between" pt={2}>
            <Typography>{materialGroup === 'from' ? 'Cost of material: ' : 'Cost transferred: '}</Typography>
            <Typography>
              {`$${setDecimalPrecision(rowTotal, {
                decimalPrecisionOverride: enableFourDecimalPrecisionInInventory ? 4 : 2,
              })}` || `$0.00${enableFourDecimalPrecisionInInventory ? '00' : ''}`}
            </Typography>
          </Stack>
        </Grid>
      </Grid>
      <Grid item xs={1}>
        {showRemoveButton(index) && (
          <IconButton aria-label="delete" onClick={() => onRemove(index)}>
            <DeleteIcon />
          </IconButton>
        )}
      </Grid>
    </Grid>
  )
}

RegradeMaterialRow.propTypes = {
  index: PropTypes.number,
  materialGroup: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  showRemoveButton: PropTypes.func,
  onRemove: PropTypes.func,
  effectiveDate: PropTypes.string,
  costOfMaterial: PropTypes.number,
  setCostOfMaterial: PropTypes.func,
  tosQuantity: PropTypes.number,
}

RegradeMaterialRow.defaultProps = {
  index: null,
  disabled: false,
  showRemoveButton: () => {},
  onRemove: () => {},
  effectiveDate: '',
  costOfMaterial: 0,
  setCostOfMaterial: () => {},
  tosQuantity: 0,
}

export default RegradeMaterialRow
